import {
  ApiSessionSourceType,
  Learnable,
  LearnableProgress,
  NextSession,
  SessionSourceInfo,
  SessionType,
  SummaryStats,
  TestSettings,
} from 'src/api/learningSessions/types';
import MemriseApi from '../MemriseApi';
import {
  fromApiLearningSessionEndResponse,
  fromApiLearningSessionResponse,
  toApiSessionType,
} from './conversion';
import * as Schema from './schema';

/**
 * The response from /learning_sessions/:session_type, converted
 * for the domain types of web_client
 */
export interface LearningSessionsDomainResponse {
  learnables: Learnable[];
  progress: LearnableProgress[];
  sessionSourceInfo: SessionSourceInfo;
  settings: TestSettings;
}

/**
 * The response from /learning_sessions/:session_type, converted
 * for the domain types of web_client
 */
export interface LearningSessionsEndDomainResponse {
  summaryStats: SummaryStats | null;
  nextSession: NextSession;
}

class LearningSessionsApiClass extends MemriseApi {
  constructor() {
    super({ endpointPrefix: '/learning_sessions' });
  }

  public async startLearningSession(
    sessionType: SessionType,
    {
      sessionSourceType,
      sessionSourceId,
    }: {
      sessionSourceType: ApiSessionSourceType;
      sessionSourceId: number;
    },
  ): Promise<LearningSessionsDomainResponse> {
    const apiSessionType = toApiSessionType(sessionType);

    const snakeParams = {
      session_source_id: sessionSourceId,
      session_source_type: sessionSourceType,
    };

    const res = await this.post<Schema.LearningSessionsResponse, Schema.LearningSessionParams>(
      `/${apiSessionType}/`,
      {
        bodyObject: snakeParams,
      },
    );

    return fromApiLearningSessionResponse(res);
  }

  public async start({
    sessionType,
    sessionSourceType,
    sessionId,
    languagePairId,
    allSessionLearnablesIdKeys,
  }: {
    sessionId: string;
    sessionType: SessionType;
    sessionSourceType: ApiSessionSourceType;
    languagePairId: number;
    allSessionLearnablesIdKeys: number[];
  }): Promise<void> {
    const apiSessionType = toApiSessionType(sessionType);

    const snakeParams: Schema.LearningSessionStartParams = {
      session_id: sessionId,
      language_pair_id: languagePairId,
      session_source_type: sessionSourceType,
      all_session_learnables_id_keys: allSessionLearnablesIdKeys,
    };

    await this.post(`/${apiSessionType}/start/`, {
      bodyObject: snakeParams,
    });
  }

  public async end({
    sessionId,
    sessionType,
    sessionSourceType,
    sessionPoints,
    sessionSourceId,
    endReason,
    allSessionLearnablesIdKeys,
  }: {
    sessionId: string;
    sessionType: SessionType;
    sessionSourceType: ApiSessionSourceType;
    sessionPoints: number;
    sessionSourceId: number;
    endReason: 'completed' | 'exited';
    allSessionLearnablesIdKeys: number[];
  }): Promise<LearningSessionsEndDomainResponse> {
    const apiSessionType = toApiSessionType(sessionType);

    const snakeParams: Schema.LearningSessionsEndParams = {
      session_points: sessionPoints,
      session_type: apiSessionType,
      session_source_type: sessionSourceType,
      session_source_id: sessionSourceId,
      session_id: sessionId,
      end_reason: endReason,
      all_session_learnables_id_keys: allSessionLearnablesIdKeys,
    };

    const res = await this.post<
      Schema.LearningSessionsEndResponse,
      Schema.LearningSessionsEndParams
    >(`/end/`, {
      bodyObject: snakeParams,
    });

    return fromApiLearningSessionEndResponse(res);
  }
}

export const LearningSessionsApi = new LearningSessionsApiClass();
